import React from 'react';

import styled from 'styled-components';

import { INutritionInfo } from '@rbi-ctg/menu';

import { SanityNutritionItem } from './types';

const TableRow = styled.tr`
  align-items: center;
`;

const Name = styled.th`
  white-space: nowrap;
  text-align: start;
  font-weight: normal;
`;

const Value = styled.td`
  text-align: end;
  white-space: nowrap;
`;

type NutritionItemProps = {
  nutritionItem: SanityNutritionItem;
  nutritionInfo: INutritionInfo;
  roundNutritionValue: (val: number) => number;
};

export const NutritionItem: React.FC<NutritionItemProps> = ({
  nutritionInfo,
  nutritionItem,
  roundNutritionValue,
  ...props
}) => {
  const primaryItemValue = nutritionInfo[nutritionItem.nutritionKey as keyof INutritionInfo] ?? '';
  const primaryItemUnit = nutritionItem.unit?.locale;
  const primaryItemName = nutritionItem.name?.locale;
  const shouldOmitPrimaryUnit = nutritionItem.omitUnitFromValue;

  if (!primaryItemValue || !primaryItemUnit || !primaryItemName) {
    return null;
  }

  const roundedPrimaryItemValue = roundNutritionValue(primaryItemValue);
  const displayPrimaryValue = shouldOmitPrimaryUnit
    ? roundedPrimaryItemValue
    : `${roundedPrimaryItemValue}${primaryItemUnit}`;

  const firstAdditionalItem = nutritionItem.additionalItems?.[0];
  const additionalItemUnit = firstAdditionalItem?.unit?.locale;
  const additionalItemValue =
    nutritionInfo[firstAdditionalItem?.nutritionKey as keyof INutritionInfo] ?? '';
  const shouldOmitAdditionalUnit = firstAdditionalItem?.omitUnitFromValue;

  const roundedAdditionalItemValue = additionalItemValue
    ? roundNutritionValue(additionalItemValue)
    : undefined;
  const displayAdditionalValue = shouldOmitAdditionalUnit
    ? roundedAdditionalItemValue
    : `${roundedAdditionalItemValue}${additionalItemUnit}`;

  return (
    <TableRow {...props}>
      <Name data-testid="nutrition-item-name" scope="row">
        {primaryItemName}
      </Name>
      <Value>
        <span data-testid="primary-item-nutrition-value">{displayPrimaryValue}</span>
        {firstAdditionalItem && (
          <span data-testid="additional-item-nutrition-value">
            {' / '}
            {displayAdditionalValue}
          </span>
        )}
      </Value>
    </TableRow>
  );
};
